/**
 * ONE definition of "is this user an admin". Previously this list was copy-pasted into ELEVEN files.
 *
 * 🚨 THE COMPARISON IS EXACT AND CASE-SENSITIVE, AND IT MUST STAY THAT WAY.
 *
 * ⛔ THE ORIGINAL REASON FOR THIS EXPIRED ON 2026-08-16 AND THE REASON BELOW REPLACES IT. This
 * header used to argue from "`users.username` has NO unique index of any kind" plus four live
 * case-collisions (`Krytecs`/`krytecs`, `ziad`/`Ziad`, `BrianLOlivier`/`Brianlolivier`,
 * `Ohhelijah00`/`ohhelijah00`). Both facts were true when written and stopped being true the SAME
 * DAY: `users_username_lower_unique` was added and the collisions were resolved. Re-measured on
 * prod 2026-09-05 — **zero** case-collisions remain and all four names are a single row each.
 *
 * ⚠ SO ANYONE READING THE OLD ARGUMENT WOULD CONCLUDE THE COMPARISON CAN SAFELY BE RELAXED, and it
 * cannot. Keep it exact for a reason that does not expire:
 *
 * ⭐ A USERNAME IS NOT AN IDENTITY BECAUSE IT CAN BE RENAMED AND RELEASED. `Jwood1988` is admin in
 * this list and is RIGHT NOW an unclaimed username — the owner renamed to `TheDesperateTrader` and
 * the old name went back into the pool. A unique index guarantees only that ONE account holds a
 * name at a time; it says nothing about WHICH account, and nothing about a name freed tomorrow.
 * (The registration reservation in app/api/register is what actually stops that name being taken;
 * this exactness is the second layer, not the first.)
 *
 * ⚠ I nearly shipped a case-insensitive version as a "robustness" improvement, before checking the
 * constraint. Check the constraint before relaxing a comparison that guards anything — and check
 * the DATE on the measurement the comment cites, because this one had gone stale by three weeks.
 *
 * ⭐ `is_admin` IS THE REAL IDENTITY. It is a column only the database can grant, it cannot be
 * changed from a profile settings page, and it is what every call site checks FIRST. The username
 * list is a legacy fallback kept so an admin without the flag is not locked out; it should be
 * retired once `is_admin` is confirmed set for everyone who needs it. See
 * `partner-application-alerts-reached-nobody`.
 */

/**
 * ⛔ THIS LIST NO LONGER GRANTS ANYTHING. RETIRED AS AN ADMIN GRANT 2026-10-21.
 *
 * It is now used for ONE job only: the signup reservation in app/api/register, which refuses these
 * names so they cannot be claimed. Reserving a name and granting admin are different jobs, and only
 * the first one still lives here.
 *
 * ⭐ WHY IT WAS SAFE TO RETIRE, measured on prod immediately before the change rather than assumed:
 *        OLD predicate (is_admin OR username list)   1 row   TheDesperateTrader
 *        NEW predicate (is_admin only)               1 row   TheDesperateTrader
 *        granted by USERNAME only, would lose admin  0 rows  NONE
 *        holds an admin string as display NAME       0 rows  NONE
 *   Nobody's access changed. The header's own condition - "retire once `is_admin` is confirmed set
 *   for everyone who needs it" - was met: everyone who needs it is one account and it has the column.
 * ⚠ The session really does carry the flag, which is the other half of why no lockout is possible:
 *   auth.ts sets `t.isAdmin = u.isAdmin` AND refreshes it from the DB on token refresh.
 * ⛔ DO NOT re-add a username arm to any grant. A username can be renamed and released - that is the
 *   whole reason `Jwood1988` became claimable in the first place (admin-username-was-claimable-0816).
 */
export const ADMIN_USERNAMES: readonly string[] = Object.freeze(['Jwood1988', 'TheDesperateTrader'])

/**
 * Exact, case-sensitive membership test.
 * ⛔ Do NOT "improve" this to a case-insensitive or trimmed comparison. `username` IS unique on
 * `lower(username)` as of 2026-08-16, so the old "a lookalike can register" argument no longer
 * applies — but a username can still be RENAMED and released, which is exactly how `Jwood1988`
 * became claimable. Exactness is the backstop behind the signup reservation; see the header.
 */
export function isAdminUsername(username: string | null | undefined): boolean {
  return !!username && ADMIN_USERNAMES.includes(username)
}

/** The shape every call site actually has; structural so this module needs no next-auth import. */
type AdminSessionLike = { user?: { isAdmin?: boolean | null; username?: string | null } | null } | null | undefined

/**
 * `is_admin` IS the rule now — there is no username fallback. Retired 2026-10-21; see the
 * ADMIN_USERNAMES header for the prod measurement showing nobody's access changed.
 * ⛔ Do not reintroduce `|| isAdminUsername(...)` here or at a call site.
 */
export function isAdminSession(session: AdminSessionLike): boolean {
  return session?.user?.isAdmin === true
}

/**
 * SQL fragment for the same rule, for the jobs and routes that pick admins in a query rather than
 * from a session. Interpolate with `sql.raw` against a table aliased `u`.
 *
 * ⚠ Same exact-match rule as above, for the same reason. The list is a compile-time constant of
 * simple identifiers, so there is nothing user-supplied to escape here.
 */
export const ADMIN_SQL_PREDICATE = `(u.is_admin = true)`
